import React, { useEffect, useState } from 'react';
import { Alert, Box, Link, TextField, Typography } from '@mui/material';
import { errorMessage } from '../participantApi';
import { Card, GradientButton, inputSx, LINK, MUTED, TextButton } from '../ui';

// поддержка Eventos — та же, что бот предлагает при проблемах со входом
const SUPPORT_URL = 'https://t.me/eventos_42';

// EV-85: вход участника — почта → код из письма. Код проверяется на сервере.
const LoginScreen = ({ api, token, onLoggedIn, onBack }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const requestCode = e => {
    e && e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    api
      .requestParticipantCode(token, { email: email.trim() })
      .then(({ data }) => {
        setStep('code');
        setCode('');
        setResendIn(data.resendAfterSeconds || 60);
      })
      .catch(err => setError(errorMessage(err)))
      .finally(() => setBusy(false));
  };

  const verify = e => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    api
      .verifyParticipantCode(token, { email: email.trim(), code: code.trim() })
      .then(({ data }) => onLoggedIn(data.sessionToken))
      .catch(err => setError(errorMessage(err)))
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>Вход в нетворкинг</Typography>

      {step === 'email' && (
        <form onSubmit={requestCode}>
          <Typography sx={{ color: MUTED, mb: 2.5 }}>
            Введите почту, которую вы указали при регистрации на мероприятие. Мы отправим на неё код для входа.
          </Typography>
          <TextField
            label='Почта'
            type='email'
            autoComplete='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
            required
            autoFocus
            sx={inputSx}
          />
          {error && (
            <Alert severity='error' sx={{ mt: 2, whiteSpace: 'pre-line', borderRadius: '12px' }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            <GradientButton type='submit' disabled={busy || !email.trim()}>
              Получить код
            </GradientButton>
            <TextButton onClick={onBack}>Назад</TextButton>
          </Box>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={verify}>
          <Typography sx={{ color: MUTED, mb: 2.5 }}>
            Если почта <b>{email.trim()}</b> зарегистрирована на мероприятие, на неё пришло письмо с кодом. Введите его
            ниже.
          </Typography>
          <TextField
            label='Код из письма'
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', style: { letterSpacing: 6, fontSize: 22, fontWeight: 700 } }}
            fullWidth
            required
            autoFocus
            sx={inputSx}
          />
          {error && (
            <Alert severity='error' sx={{ mt: 2, whiteSpace: 'pre-line', borderRadius: '12px' }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            <GradientButton type='submit' disabled={busy || code.length < 6}>
              Войти
            </GradientButton>
            <TextButton onClick={() => requestCode()} disabled={busy || resendIn > 0}>
              {resendIn > 0 ? `Отправить ещё раз (${resendIn})` : 'Отправить ещё раз'}
            </TextButton>
            <TextButton
              onClick={() => {
                setStep('email');
                setError(null);
              }}
            >
              Другая почта
            </TextButton>
          </Box>
          <Typography variant='body2' sx={{ color: MUTED, mt: 3 }}>
            Письмо не пришло? Проверьте папку «Спам» и правильность адреса — нужна почта, указанная при регистрации
            на мероприятие.
          </Typography>
          <Typography variant='body2' sx={{ color: MUTED, mt: 1 }}>
            Если нужно изменить почту — напишите нам:{' '}
            <Link href={SUPPORT_URL} target='_blank' rel='noopener noreferrer' sx={{ color: LINK }}>
              t.me/eventos_42
            </Link>
          </Typography>
        </form>
      )}
    </Card>
  );
};

export default LoginScreen;
